import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, TextField, IconButton, Typography, Box, Chip, FormControl, 
  InputLabel, Select, MenuItem, LinearProgress, Backdrop, CircularProgress, 
  InputAdornment, Autocomplete, Stack, Divider, Tooltip
} from '@mui/material';
import { 
  AddCircleOutline as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import axios from '../../api/axiosConfig';

const CommonCodeManage = () => {
  const THEME_NAVY = '#1a233a';
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // 조회 여부 상태 추가
  
  // --- 검색/필터 상태 ---
  const [filter, setFilter] = useState({ total: '', group: '', detail: '' });

  // --- 입력 폼 상태 ---
  const initialCodeState = () => ({
    groupCode: '',
    groupCodeName: '',
    detailCode: '',
    detailCodeName: '',
    description: '',
    useYn: 'Y',
    useStartDate: new Date().toISOString().split('T')[0],
    useEndDate: '9999-12-31'
  });

  const [currentCode, setCurrentCode] = useState(initialCodeState());
  const [isEditMode, setIsEditMode] = useState(false);
  const [isGroupSelected, setIsGroupSelected] = useState(false);

  // --- 데이터 로딩 함수 ---
  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setHasSearched(true); // 조회 버튼 클릭 시 true로 변경
    try {
      const res = await axios.get('/api/common-code');
      setCodes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("데이터 로딩 실패", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // [수정] useEffect 자동 호출 제거 (화면 진입 시 자동 조회 안 함)

  // Autocomplete를 위한 그룹 옵션 추출 (조회된 데이터가 있을 때만 작동)
  const groupOptions = useMemo(() => {
    const map = new Map();
    codes.forEach(item => {
      if (item.groupCode && !map.has(item.groupCode)) {
        map.set(item.groupCode, item.groupCodeName);
      }
    });
    return Array.from(map.entries()).map(([groupCode, groupCodeName]) => ({
      groupCode,
      groupCodeName
    }));
  }, [codes]);

  // --- 핸들러 함수 ---
  const handleEdit = (code) => {
    setIsEditMode(true);
    setIsGroupSelected(true);
    setCurrentCode({ ...code });
  };

  const resetForm = () => {
    setIsEditMode(false);
    setIsGroupSelected(false);
    setCurrentCode(initialCodeState());
  };

  const handleResetSearch = () => {
    setFilter({ total: '', group: '', detail: '' });
    setCodes([]);
    setHasSearched(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentCode.groupCode || !currentCode.detailCode) {
      alert("분류코드와 세부코드는 필수 입력 항목입니다.");
      return;
    }
    try {
      if (isEditMode) {
        await axios.put(`/api/common-code/${currentCode.commonCodePk}`, currentCode);
        alert("수정되었습니다.");
      } else {
        await axios.post('/api/common-code', currentCode);
        alert("등록되었습니다.");
      }
      resetForm();
      fetchCodes();
    } catch (err) {
      alert("저장 실패: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      try {
        await axios.delete(`/api/common-code/${id}`);
        fetchCodes();
      } catch (err) { alert("삭제 실패"); }
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // --- 검색 필터링 로직 ---
  const filteredCodes = useMemo(() => {
    return codes.filter(c => {
      const matchTotal = !filter.total || [c.groupCode, c.groupCodeName, c.detailCode, c.detailCodeName].some(v => v?.toLowerCase().includes(filter.total.toLowerCase()));
      const matchGroup = !filter.group || c.groupCode.toLowerCase().includes(filter.group.toLowerCase()) || c.groupCodeName.toLowerCase().includes(filter.group.toLowerCase());
      const matchDetail = !filter.detail || c.detailCode.toLowerCase().includes(filter.detail.toLowerCase()) || c.detailCodeName.toLowerCase().includes(filter.detail.toLowerCase());
      return matchTotal && matchGroup && matchDetail;
    });
  }, [codes, filter]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading && hasSearched}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: THEME_NAVY, fontSize: 30 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>시스템 공통코드 관리</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        
        {/* --- 좌측: 등록/수정 섹션 --- */}
        <Paper elevation={3} sx={{ 
          p: 3, 
          width: { xs: '100%', lg: '400px' }, 
          height: 'fit-content', 
          borderTop: isEditMode ? '4px solid #ed6c02' : `4px solid ${THEME_NAVY}`, 
          borderRadius: 2 
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              {isEditMode ? <EditIcon sx={{ mr: 1 }} color="warning" /> : <AddIcon sx={{ mr: 1 }} color="primary" />}
              {isEditMode ? '코드 정보 수정' : '신규 코드 등록'}
            </Typography>
            {isEditMode && <IconButton size="small" onClick={resetForm}><CloseIcon /></IconButton>}
          </Box>
          <Divider sx={{ mb: 2 }} />

          <form onSubmit={handleSave}>
            <Autocomplete
              freeSolo
              options={groupOptions}
              getOptionLabel={(option) => (typeof option === 'string' ? option : (option.groupCode || ''))}
              disableClearable={isEditMode}
              value={groupOptions.find(o => o.groupCode === currentCode.groupCode) || currentCode.groupCode || null}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  setCurrentCode(prev => ({ ...prev, groupCode: newValue.groupCode, groupCodeName: newValue.groupCodeName }));
                  setIsGroupSelected(true);
                } else {
                  setCurrentCode(prev => ({ ...prev, groupCode: '', groupCodeName: '' }));
                  setIsGroupSelected(false);
                }
              }}
              onInputChange={(event, newInputValue) => {
                if (event?.type === 'change' && !isEditMode && !isGroupSelected) {
                  setCurrentCode(prev => ({ ...prev, groupCode: newInputValue }));
                }
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="분류코드 (선택/입력)" 
                  required size="small" margin="normal"
                  sx={{ bgcolor: (isEditMode || isGroupSelected) ? '#f5f5f5' : 'inherit' }}
                  InputProps={{ ...params.InputProps, readOnly: isEditMode || isGroupSelected }}
                />
              )}
            />

            <TextField 
              label="분류코드명" fullWidth size="small" margin="normal"
              value={currentCode.groupCodeName || ''} 
              onChange={(e) => setCurrentCode({...currentCode, groupCodeName: e.target.value})} 
              InputProps={{ readOnly: isEditMode || isGroupSelected }}
              sx={{ bgcolor: (isEditMode || isGroupSelected) ? '#f5f5f5' : 'inherit' }}
            />

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField label="세부코드" fullWidth required size="small" value={currentCode.detailCode} onChange={(e) => setCurrentCode({...currentCode, detailCode: e.target.value})} />
              <TextField label="세부코드명" fullWidth size="small" value={currentCode.detailCodeName} onChange={(e) => setCurrentCode({...currentCode, detailCodeName: e.target.value})} />
            </Stack>
            
            <FormControl fullWidth size="small" margin="normal">
              <InputLabel>사용여부</InputLabel>
              <Select value={currentCode.useYn} label="사용여부" onChange={(e) => setCurrentCode({...currentCode, useYn: e.target.value})}>
                <MenuItem value="Y">사용 (Y)</MenuItem>
                <MenuItem value="N">미사용 (N)</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField label="사용시작일" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={currentCode.useStartDate} onChange={(e) => setCurrentCode({...currentCode, useStartDate: e.target.value})} />
              <TextField label="사용종료일" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={currentCode.useEndDate} onChange={(e) => setCurrentCode({...currentCode, useEndDate: e.target.value})} />
            </Stack>
            
            <TextField label="설명" fullWidth multiline rows={2} size="small" margin="normal" value={currentCode.description || ''} onChange={(e) => setCurrentCode({...currentCode, description: e.target.value})} />

            <Button fullWidth variant="contained" type="submit" sx={{ mt: 3, py: 1.2, backgroundColor: isEditMode ? '#ed6c02' : THEME_NAVY, fontWeight: 'bold' }}>
              {isEditMode ? '수정 내용 저장' : '공통코드 저장'}
            </Button>
          </form>
        </Paper>

        {/* --- 우측: 검색 및 목록 섹션 --- */}
        <Box sx={{ flexGrow: 1 }}>
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', backgroundColor: '#fdfdfd', borderRadius: 2 }}>
            <TextField 
              size="small" placeholder="통합 검색 (코드, 명칭)" 
              value={filter.total} 
              onChange={(e) => setFilter({...filter, total: e.target.value})} 
              onKeyDown={(e) => e.key === 'Enter' && fetchCodes()}
              sx={{ minWidth: '200px' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} 
            />
            <TextField label="분류코드/명" size="small" sx={{ width: '150px' }} value={filter.group} onChange={(e) => setFilter({...filter, group: e.target.value})} />
            <TextField label="세부코드/명" size="small" sx={{ width: '150px' }} value={filter.detail} onChange={(e) => setFilter({...filter, detail: e.target.value})} />
            
            {/* [수정] 조회 및 초기화 버튼 배치 변경 */}
            <Button 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} 
              onClick={fetchCodes}
              sx={{ bgcolor: THEME_NAVY }}
            >
              조회
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={handleResetSearch}>초기화</Button>
          </Paper>

          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, position: 'relative', maxHeight: 'calc(100vh - 405px)' }}>
            {loading && <LinearProgress sx={{ position: 'absolute', top: 0, width: '100%', zIndex: 1 }} />}
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f4f6f8', fontWeight: 'bold' }}>분류코드(명)</TableCell>
                  <TableCell sx={{ bgcolor: '#f4f6f8', fontWeight: 'bold' }}>세부코드(명)</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#f4f6f8', fontWeight: 'bold' }}>상태</TableCell>
                  <TableCell sx={{ bgcolor: '#f4f6f8', fontWeight: 'bold' }}>등록 정보</TableCell>
                  <TableCell sx={{ bgcolor: '#f4f6f8', fontWeight: 'bold' }}>수정 정보</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#f4f6f8', fontWeight: 'bold' }}>액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasSearched ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 15 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                      <Typography color="text.secondary" fontWeight="bold">조회 버튼을 클릭하여 데이터를 확인하세요.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCodes.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 10 }}>데이터가 없습니다.</TableCell></TableRow>
                ) : (
                  filteredCodes.map((row) => (
                    <TableRow key={row.commonCodePk} hover sx={{ backgroundColor: currentCode.commonCodePk === row.commonCodePk ? '#fff4e5' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.groupCode}</Typography>
                        <Typography variant="caption" color="textSecondary">{row.groupCodeName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.description || ''}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.detailCode}</Typography>
                            <Typography variant="caption" color="textSecondary">{row.detailCodeName}</Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.useYn === 'Y' ? "사용" : "미사용"} color={row.useYn === 'Y' ? "success" : "default"} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{row.createUser || 'SYSTEM'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(row.createDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{row.updateUser || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.updateDate ? formatDateTime(row.updateDate) : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.commonCodePk)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {hasSearched && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'right', color: 'text.secondary' }}>
              총 {filteredCodes.length}건의 코드가 조회되었습니다.
            </Typography>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default CommonCodeManage;